import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

import { ThemeProvider } from './context/ThemeContext';
import ScrollToTop from './components/ScrollToTop';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Home from './pages/Home';



// Lazy load everything except Home
const Navbar = lazy(() => import('./components/Navbar'));
const Footer = lazy(() => import('./components/Footer'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Career = lazy(() => import('./pages/Career'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Projects = lazy(() => import('./pages/Projects'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const Pricing = lazy(() => import('./pages/Pricing'));

// Lazy load service pages
const CustomWebsiteDesign = lazy(() => import('./pages/services/CustomWebsiteDesign'));
const WordPressDesign = lazy(() => import('./pages/services/WordpressDesign'));
const MobileResponsiveDesign = lazy(() => import('./pages/services/MobileResponsiveDesign'));
const ECommerceWebsite = lazy(() => import('./pages/services/EcommerceWebsite'));
const AppDevelopment = lazy(() => import('./pages/services/AppDevelopment'));
const CloudServices = lazy(() => import('./pages/services/CloudServices'));
const ContentCreation = lazy(() => import('./pages/services/ContentCreation'));
const SocialMediaAdvertising = lazy(() => import('./pages/services/SocialMediaAdvertising'));
const InfluencerMarketing = lazy(() => import('./pages/services/InfluencerMarketing'));
const AIMachineLearning = lazy(() => import('./pages/services/AIMachineLearning'));
const KeywordResearch = lazy(() => import('./pages/services/KeywordResearch'));
const OnPageSeo = lazy(() => import('./pages/services/OnPageSeo'));
const OffPageSeo = lazy(() => import('./pages/services/OffPageSeo'));
const TechnicalSeo = lazy(() => import('./pages/services/TechnicalSeo'));
const SearchEngineOptimization = lazy(() => import('./pages/services/SearchEngineOptimization'));
const LocalKeywordResearch = lazy(() => import('./pages/services/LocalKeywordResearch'));
const MapsCitation = lazy(() => import('./pages/services/MapsCitation'));
const AdCreationTesting = lazy(() => import('./pages/services/AdCreationTesting'));
const CampaignSetup = lazy(() => import('./pages/services/CampaignSetup'));

// Lazy load landing pages
const ConstructionLandingPage = lazy(() => import('./Landing Pages/Construction/ConstructionLandingPage'));
const Construction2 = lazy(() => import('./Landing Pages/Construction2/Construction2'));
const Architecture = lazy(() => import('./Landing Pages/Architecture/Architecture'));
const ArchitectureLanding = lazy(() => import('./Landing Pages/Architecture2/ArchitectureLanding'));
const SolarLanding = lazy(() => import('./Landing Pages/Energy/SolarLanding'));
const DayCare = lazy(() => import('./Landing Pages/DayCare/DaycareLanding'));
const Tourist = lazy(() => import('./Landing Pages/Tourist/TouristLandingPage'));

// Admin routes — lazy loaded, and AdminArea below scopes AuthProvider
// so Supabase (GoTrueClient / RealtimeChannel) never ships in the public bundle
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminArea = lazy(() => import('./components/Adminarea'));

// Routes that should render standalone, without the site Navbar/Footer
const STANDALONE_ROUTES = [
  '/projects/construction-website',
  '/projects/construction-company-website',
  '/projects/architecture-studio-website',
  '/projects/modern-architecture-firm',
  '/projects/solar-energy-website',
  '/projects/kids-daycare-website',
  '/projects/travel-website'
];

function AppContent() {
 
  const location = useLocation();
  const isStandalone = STANDALONE_ROUTES.includes(location.pathname);

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <ScrollToTop />
      {!isStandalone && (
        <Suspense fallback={<div></div>}>
          <Navbar />
        </Suspense>
      )}
      <main id="main-content">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* ... existing routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/custom-web-development" element={<CustomWebsiteDesign />} />
            <Route path="/services/wordpress-web-development" element={<WordPressDesign />} />
            <Route path="/services/responsive-web-development" element={<MobileResponsiveDesign />} />
            <Route path="/services/ecommerce-website-development" element={<ECommerceWebsite />} />
            <Route path="/services/custom-app-development" element={<AppDevelopment />} />
            <Route path="/services/cloud-solutions-services" element={<CloudServices />} />
            <Route path="/services/content-creation-services" element={<ContentCreation />} />
            <Route path="/services/social-media-advertising" element={<SocialMediaAdvertising />} />
            <Route path="/services/influencer-marketing" element={<InfluencerMarketing />} />
            <Route path="/services/ai-and-ml-services" element={<AIMachineLearning />} />
            <Route path="/services/keyword-research-services" element={<KeywordResearch />} />
            <Route path="/services/on-page-seo-services" element={<OnPageSeo />} />
            <Route path="/services/off-page-seo-services" element={<OffPageSeo />} />
            <Route path="/services/technical-seo-services" element={<TechnicalSeo />} />
            <Route path="/services/seo-services" element={<SearchEngineOptimization />} />
            <Route path="/services/local-keyword-research-services" element={<LocalKeywordResearch />} />
            <Route path="/services/map-and-citation-services" element={<MapsCitation />} />
            <Route path="/services/ad-creation-services" element={<AdCreationTesting />} />
            <Route path="/services/campaign-setup-services" element={<CampaignSetup />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/career" element={<Career />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:slug" element={<BlogDetail />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path='/projects/construction-website' element={<ConstructionLandingPage />} />
            <Route path='/projects/construction-company-website' element={<Construction2 />} />
            <Route path='/projects/architecture-studio-website' element={<Architecture />} />
            <Route path='/projects/modern-architecture-firm' element={<ArchitectureLanding />} />
            <Route path='/projects/solar-energy-website' element={<SolarLanding />} />
            <Route path='/projects/kids-daycare-website' element={<DayCare />} />
            <Route path='/projects/travel-website' element={<Tourist />} />

            {/*
              Admin routes: AuthProvider + ProtectedRoute are now bundled inside
              AdminArea (a lazy chunk), so Supabase auth code only downloads when
              someone actually visits /adminLogin or /adminDashboard.
            */}
            <Route
              path='/adminLogin'
              element={
                <AdminArea>
                  <AdminLogin />
                </AdminArea>
              }
            />
            <Route
              path='/adminDashboard'
              element={
                <AdminArea requireAuth>
                  <AdminDashboard />
                </AdminArea>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isStandalone && (
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <HelmetProvider>
        <Router>
          <AppContent />
        </Router>
      </HelmetProvider>
    </ThemeProvider>
  );
}

function LoadingFallback() {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-primary)'
    }}>
      <div className="loader" style={{
        width: 40,
        height: 40,
        border: '3px solid var(--accent-light)',
        borderTopColor: 'var(--accent)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function NotFound() {
  return (
    <main style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 24,
      textAlign: 'center',
      padding: '120px 24px',
      background: 'var(--gradient-hero)',
    }}>
      
      <h1 style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', fontWeight: 900, letterSpacing: '-0.04em', background: 'var(--gradient-text)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>404</h1>
      <p style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Page Not Found</p>
      <p style={{ color: 'var(--text-secondary)', maxWidth: 400 }}>The page you're looking for doesn't exist or has been moved.</p>
      <a href="/" className="btn-primary">← Back to Home</a>
    </main>
  );
}

export default App;